import { useCallback, useContext, useEffect, useState } from "react";
import {
  authorOption,
  robotsOption,
  statusOption,
  mainUrl,
} from "../../../../constant";
import { useLocale, useService } from "../../../../hooks";
import { NavigatorContext } from "../../../../context";

export const useAddEvent = () => {
  const { t } = useLocale();
  const { navigateTo } = useContext(NavigatorContext);
  const { fetchByAttr, save, update } = useService("Data", "event");
  const { getUsers } = useService("User");
  const { setTitle } = useService("Meta");
  const { isEmpty, showAlert, slugify, generateUID } = useService("Misc");
  const { urlToSplitData, getLocationData } = useService("Router");
  const urlData = urlToSplitData(getLocationData().pathname);
  const keywordOptions = [];
  const categoryOptions = [];
  const tagOptions = [];
  const [activeRobotsIndex, setactiveRobotsIndex] = useState<number>(0);
  const [activeStatusIndex, setactiveStatusIndex] = useState<number>(0);
  const [activeAuthorIndex, setactiveAuthorIndex] = useState<number>(0);
  const [image, setimage] = useState<string>("[]");
  const [uid, setuid] = useState<string>("");
  const [featuredVideo, setfeaturedVideo] = useState<string>("");
  const [renderSlug, setrenderSlug] = useState<boolean>(true);
  const [mode, setmode] = useState<"add" | "edit" | "view">("add");
  const [title, settitle] = useState<string>("");
  const [slug, setslug] = useState<string>("");
  const [content, setcontent] = useState<string>("");
  const [excerpt, setexcerpt] = useState<string>("");
  const [css, setcss] = useState<string>("");
  const [js, setjs] = useState<string>("");
  const [canonical, setcanonical] = useState<string>("");
  const [robots, setrobots] = useState<string>("");
  const [metaTitle, setmetaTitle] = useState<string>("");
  const [metaDescription, setmetaDescription] = useState<string>("");
  const [metaKeyword, setmetaKeyword] = useState(null);
  const [publishDate, setpublishDate] = useState(new Date());
  const [assignWorkers, setassignWorkers] = useState([]);
  const [status, setstatus] = useState(null);
  const [category, setcategory] = useState(null);
  const [location, setlocation] = useState<string>("");
  const [tag, settag] = useState(null);

  setTitle("Add Event", "admin");

  const getUsersData = useCallback(async () => {
    let tableData = [];
    const result = await getUsers();
    if (result.status) {
      if (result.data?.length === 0)
        showAlert({ type: "error", msg: "No user added yet!" });
      else {
        result.data.forEach((result) => {
          tableData.push({ label: result.name, value: result.uid });
        });
        setassignWorkers(tableData);
      }
    }
  }, [getUsers, showAlert]);

  const getEventData = useCallback(
    async (uid) => {
      const result = await fetchByAttr({ uid });
      if (result.status) {
        const eventData = result.data[0];
        settag(isEmpty(eventData.tag) ? [] : JSON.parse(eventData.tag));
        setcategory(
          isEmpty(eventData.category) ? [] : JSON.parse(eventData.category)
        );
        setmetaKeyword(
          isEmpty(eventData.metaKeyword)
            ? []
            : JSON.parse(eventData.metaKeyword)
        );
        setactiveRobotsIndex(
          robotsOption.findIndex((e) => e.value === eventData.robots)
        );
        setactiveStatusIndex(
          statusOption.findIndex((e) => e.value === eventData.status)
        );
        setactiveAuthorIndex(
          authorOption.findIndex((e) => e.value === eventData.status)
        );
        settitle(eventData.title);
        setmode("edit");
        setslug(eventData.slug);
        setfeaturedVideo(eventData.featuredVideo ?? "");
        setcontent(eventData.content);
        setexcerpt(eventData.excerpt);
        setcss(eventData.css);
        setjs(eventData.js);
        setcanonical(eventData.canonical);
        setrobots(eventData.robots);
        setmetaDescription(eventData.metaDescription);
        setmetaTitle(eventData.metaTitle);
        setpublishDate(new Date(eventData.publishDate));
        setstatus(eventData.status);
        setuid(eventData.uid);
        setimage(eventData.image);
        setlocation(eventData.location ?? "");
      } else showAlert({ type: "error", msg: "No event added yet!" });
    },
    [fetchByAttr, isEmpty, showAlert]
  );

  useEffect(() => {
    setstatus(statusOption[activeStatusIndex].value);
    if (urlData[3] === "edit") {
      getEventData(urlData[4]);
    }
    getUsersData();
  }, [activeStatusIndex, getEventData, getUsersData, urlData]);

  const addEvent = useCallback(
    async (e) => {
      e.preventDefault();
      let error = false,
        errorMsg = "",
        eventObject = {
          title,
          slug,
          featuredVideo,
          content,
          excerpt,
          css,
          js,
          canonical,
          robots,
          metaDescription,
          metaKeyword: JSON.stringify(metaKeyword),
          publishDate,
          status,
          category: JSON.stringify(category),
          tag: JSON.stringify(tag),
          image,
          location,
          uid: mode === "add" ? generateUID() : uid,
        };
      if (title === "") {
        errorMsg = t("errorMsg.required.title");
        error = true;
      } else if (slug === "") {
        errorMsg = t("errorMsg.required.slug");
        error = true;
      } else if (content === "") {
        errorMsg = t("errorMsg.required.content");
        error = true;
      } else if (excerpt === "") {
        errorMsg = t("errorMsg.required.excerpt");
        error = true;
      } else if (image === "") {
        errorMsg = t("errorMsg.required.image");
        error = true;
      }
      if (!error) {
        if (mode === "add") {
          const result = await save(eventObject);
          if (result.status) {
            showAlert({ type: "success", msg: "Event added successfully!" });
            navigateTo({ route: "/admin/event/list" });
          } else showAlert({ type: "error", msg: "Unable to add event!" });
        } else {
          let data = {
            query: JSON.stringify({ uid }),
            eventData: JSON.stringify(eventObject),
          };
          const result = await update(data);
          if (result.status) {
            showAlert({ type: "success", msg: "Event updated successfully!" });
            navigateTo({ route: "/admin/event/list" });
          } else showAlert({ type: "error", msg: "Unable to update event!" });
        }
      } else showAlert({ type: "error", msg: errorMsg });
    },
    [
      canonical,
      category,
      content,
      css,
      excerpt,
      featuredVideo,
      generateUID,
      image,
      js,
      location,
      metaDescription,
      metaKeyword,
      mode,
      navigateTo,
      publishDate,
      robots,
      save,
      showAlert,
      slug,
      status,
      t,
      tag,
      title,
      uid,
      update,
    ]
  );

  const addTitle = useCallback(
    (e) => {
      let tempTitle = e.target.value,
        tempSlug = slug;
      if (renderSlug) {
        tempSlug = slugify(tempTitle);
      }
      settitle(tempTitle);
      setslug(tempSlug);
    },
    [renderSlug, slug, slugify]
  );

  const toggleRenderSlug = useCallback(
    () => setrenderSlug(!renderSlug),
    [renderSlug]
  );

  let pageTitle = mode + " Event",
    activeRobots = robotsOption[activeRobotsIndex],
    activeStatus = statusOption[activeStatusIndex];
  robotsOption.forEach((e) => {
    if (e.value === robots) activeRobots = e;
  });
  statusOption.forEach((e) => {
    if (e.value === status) activeStatus = e;
  });
  if (canonical === "") setcanonical(mainUrl + urlData[2] + "/" + slug);
  return {
    statusOption,
    robotsOption,
    pageTitle,
    addEvent,
    title,
    addTitle,
    slug,
    setslug,
    content,
    setcontent,
    excerpt,
    setexcerpt,
    location,
    setlocation,
    css,
    setcss,
    js,
    setjs,
    canonical,
    setcanonical,
    activeRobots,
    setrobots,
    metaDescription,
    setmetaDescription,
    metaTitle,
    setmetaTitle,
    setmetaKeyword,
    keywordOptions,
    metaKeyword,
    renderSlug,
    toggleRenderSlug,
    setpublishDate,
    publishDate,
    activeStatus,
    setstatus,
    category,
    setcategory,
    categoryOptions,
    tag,
    settag,
    featuredVideo,
    setfeaturedVideo,
    tagOptions,
    image,
    setimage,
  };
};
